// src/pages/ApePdfHistoryPage.jsx (New page/component for viewing PDF history)
import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "tailwindcss/tailwind.css";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { useTable, usePagination, useSortBy, useGlobalFilter, useFilters, useRowSelect } from "react-table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEye,
  faEdit,
  faTrash,
  faEnvelope,
  faFilePdf,
  faCheck,
  faRedo, // For regenerate
  faDownload,
} from "@fortawesome/free-solid-svg-icons";
import {
  MagnifyingGlassIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  PlusIcon,
  CalendarIcon,
  UserIcon,
  TagIcon,
  CubeIcon,
} from "@heroicons/react/24/outline";
import clsx from "clsx";
import { BASE_URL } from "../../config";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Swal from "sweetalert2";
import PlanBadge from "../PlanBadge"; // Assuming you have this or similar

const Badge = ({ children, color = "gray" }) => {
  const colors = {
    gray: "bg-gray-100 text-gray-700 ring-gray-200",
    green: "bg-emerald-100 text-emerald-700 ring-emerald-200",
    yellow: "bg-amber-100 text-amber-700 ring-amber-200",
    red: "bg-rose-100 text-rose-700 ring-rose-200",
    blue: "bg-blue-100 text-blue-700 ring-blue-200",
  };
  return (
    <span
      className={clsx(
        "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ring-1",
        colors[color]
      )}
    >
      {children}
    </span>
  );
};

const EmptyState = ({
  title = "No PDF History",
  subtitle = "Generate some PDFs to see history here.",
}) => {
  const navigate = useNavigate();

  return (
    <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-gray-300 bg-white p-12 text-center shadow-sm">
      <svg
        viewBox="0 0 24 24"
        className="mb-4 h-12 w-12 text-gray-400"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M19 7.5a2.25 2.25 0 01-4.5 0m0 0a2.25 2.25 0 01-4.5 0m4.5 0v6.75m0 0a2.25 2.25 0 01-4.5 0M9 7.5a2.25 2.25 0 014.5 0m0 0v6.75m0 0a2.25 2.25 0 01-4.5 0M9 7.5a2.25 2.25 0 014.5 0m-9 6.75a2.25 2.25 0 014.5 0m0 0a2.25 2.25 0 01-4.5 0m4.5 0v6.75m0 0a2.25 2.25 0 01-4.5 0M9 18.75a2.25 2.25 0 014.5 0m0 0a2.25 2.25 0 01-4.5 0m4.5 0v-6.75"
        />
      </svg>

      <div className="text-base font-semibold text-gray-900">{title}</div>
      <div className="mt-1 text-sm text-gray-500">{subtitle}</div>

      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.97 }}
        onClick={() => navigate("/ape-scheduler/add")} // Or link to generation page
        className="mt-6 inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-blue-500 px-5 py-2.5 text-sm font-semibold text-white shadow-lg hover:from-blue-700 hover:to-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-1"
      >
        <PlusIcon className="h-5 w-5" />
        Generate PDF
      </motion.button>
    </div>
  );
};

const FilterSection = ({ onFilterChange, filters, pdfTypes, statuses, emailStatuses }) => {
  const [filterPdfType, setFilterPdfType] = useState(filters.pdf_type || '');
  const [filterStatus, setFilterStatus] = useState(filters.status || '');
  const [filterEmailStatus, setFilterEmailStatus] = useState(filters.email_status || '');
  const [dateFrom, setDateFrom] = useState(filters.date_from ? new Date(filters.date_from) : null);
  const [dateTo, setDateTo] = useState(filters.date_to ? new Date(filters.date_to) : null);
  const [search, setSearch] = useState(filters.search || '');

  const handleFilterChange = () => {
    onFilterChange({
      pdf_type: filterPdfType,
      status: filterStatus,
      email_status: filterEmailStatus,
      date_from: dateFrom ? dateFrom.toISOString().split('T')[0] : null,
      date_to: dateTo ? dateTo.toISOString().split('T')[0] : null,
      search,
    });
  };

  return (
    <div className="bg-white shadow-sm ring-1 ring-gray-900/5 sm:rounded-xl md:px-8 md:py-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-6">
        <div>
          <label className="block text-sm font-medium leading-6 text-gray-900 mb-1">PDF Type</label>
          <select
            value={filterPdfType}
            onChange={(e) => setFilterPdfType(e.target.value)}
            onBlur={handleFilterChange}
            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
          >
            <option value="">All Types</option>
            {pdfTypes.map((type) => (
              <option key={type.value} value={type.value}>{type.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium leading-6 text-gray-900 mb-1">Status</label>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            onBlur={handleFilterChange}
            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
          >
            <option value="">All Status</option>
            {statuses.map((status) => (
              <option key={status.value} value={status.value}>{status.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium leading-6 text-gray-900 mb-1">Email Status</label>
          <select
            value={filterEmailStatus}
            onChange={(e) => setFilterEmailStatus(e.target.value)}
            onBlur={handleFilterChange}
            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
          >
            {emailStatuses.map((status) => (
              <option key={status.value} value={status.value}>{status.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium leading-6 text-gray-900 mb-1">Search</label>
          <div className="relative">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
            </div>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleFilterChange()}
              className="block w-full rounded-md border-0 py-1.5 pl-10 pr-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
              placeholder="File name or ID..."
            />
          </div>
        </div>
        <div className="flex items-end">
          <label className="block text-sm font-medium leading-6 text-gray-900 mb-1">Date From</label>
          <DatePicker
            selected={dateFrom}
            onChange={(date) => setDateFrom(date)}
            onCalendarClose={handleFilterChange}
            dateFormat="yyyy-MM-dd"
            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
            placeholderText="From Date"
          />
        </div>
        <div className="flex items-end">
          <label className="block text-sm font-medium leading-6 text-gray-900 mb-1">Date To</label>
          <DatePicker
            selected={dateTo}
            onChange={(date) => setDateTo(date)}
            onCalendarClose={handleFilterChange}
            dateFormat="yyyy-MM-dd"
            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
            placeholderText="To Date"
          />
        </div>
      </div>
      <div className="mt-4 flex justify-end">
        <button
          onClick={handleFilterChange}
          className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
        >
          Apply Filters
        </button>
      </div>
    </div>
  );
};

const ApePdfHistoryPage = () => {
  const [tableData, setTableData] = useState({ data: [], pagination: {} });
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ page: 1, per_page: 10, pdf_type: '', status: '', email_status: '', date_from: null, date_to: null, search: '' });
  const [pdfTypes] = useState([{ value: 'corporate', label: 'Corporate' }, { value: 'provider', label: 'Provider' }]);
  const [statuses] = useState([{ value: 'generated', label: 'Generated' }, { value: 'failed', label: 'Failed' }]);
  const [emailStatuses] = useState([{ value: '', label: 'All' }, { value: 'pending', label: 'Pending' }, { value: 'sent', label: 'Sent' }, { value: 'failed', label: 'Failed' }]);

  const navigate = useNavigate();

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem("authentication/token")}`,
  });

  const fetchPdfHistory = async (page = 1, filters = {}) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ ...filters, page, per_page: filters.per_page || 10 });
      const res = await axios.get(`${BASE_URL}/ape-scheduler/pdf-history?${params}`, {
        headers: getAuthHeaders(),
      });
      setTableData(res.data);
    } catch (err) {
      toast.error("Failed to load PDF history");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPdfHistory(filters.page, filters);
  }, []);

  const handleFilterChange = (newFilters) => {
    setFilters((prev) => ({ ...prev, ...newFilters, page: 1 }));
    fetchPdfHistory(1, { ...filters, ...newFilters });
  };

  const handlePageChange = (page) => {
    setFilters((prev) => ({ ...prev, page }));
    fetchPdfHistory(page, filters);
  };

  const handleRegenerate = async (exportId) => {
    try {
      await axios.post(`${BASE_URL}/ape-scheduler/pdf-history/${exportId}/regenerate`, {}, { headers: getAuthHeaders() });
      toast.success("URL regenerated successfully!");
      fetchPdfHistory(filters.page, filters); // Refresh table
    } catch (err) {
      toast.error("Failed to regenerate URL");
    }
  };

  const handleDownload = (signedUrl, originalFileName) => {
    const link = document.createElement('a');
    link.href = signedUrl;
    link.download = originalFileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleResendEmail = async (setupId, pdfType) => {
    try {
      await axios.post(`${BASE_URL}/ape-scheduler/send-email/setup/${setupId}?type=${pdfType}`, {}, { headers: getAuthHeaders() });
      toast.success(`Resend email to ${pdfType} queued!`);
      fetchPdfHistory(filters.page, filters); // Refresh
    } catch (err) {
      toast.error("Failed to resend email");
    }
  };

  const columns = useMemo(
    () => [
      {
        Header: 'PDF Type',
        accessor: 'pdf_type',
        Cell: ({ value }) => <Badge color={value === 'corporate' ? 'blue' : 'green'}>{value.toUpperCase()}</Badge>,
      },
      {
        Header: 'Original File Name',
        accessor: 'original_file_name',
        Cell: ({ value }) => (
          <span className="truncate max-w-[150px] block" title={value}>{value}</span>
        ),
      },
      {
        Header: 'Status',
        accessor: 'status',
        Cell: ({ value }) => <Badge color={value === 'generated' ? 'green' : 'red'}>{value.toUpperCase()}</Badge>,
      },
      {
        Header: 'Email Status',
        accessor: 'email_status',
        Cell: ({ value }) => <Badge color={value === 'sent' ? 'green' : value === 'failed' ? 'red' : 'yellow'}>{value || 'N/A'}</Badge>,
      },
      {
        Header: 'Created At',
        accessor: 'created_at',
        Cell: ({ value }) => new Date(value).toLocaleDateString(),
      },
      {
        Header: 'Expires At',
        accessor: 'url_expires_at',
        Cell: ({ value }) => new Date(value).toLocaleDateString(),
      },
      {
        Header: 'Actions',
        accessor: 'actions',
        Cell: ({ row }) => (
          <div className="flex space-x-2">
            <button
              onClick={() => handleDownload(row.original.signed_url, row.original.original_file_name)}
              className="text-blue-600 hover:text-blue-900 p-1"
              title="Download PDF"
            >
              <FontAwesomeIcon icon={faDownload} />
            </button>
            <button
              onClick={() => handleRegenerate(row.original.export_id)}
              className="text-green-600 hover:text-green-900 p-1"
              title="Regenerate URL"
              disabled={new Date(row.original.url_expires_at) > new Date()}
            >
              <FontAwesomeIcon icon={faRedo} />
            </button>
            <button
              onClick={() => handleResendEmail(row.original.setup_id, row.original.pdf_type)}
              className="text-orange-600 hover:text-orange-900 p-1"
              title={`Resend Email to ${row.original.pdf_type}`}
            >
              <FontAwesomeIcon icon={faEnvelope} />
            </button>
          </div>
        ),
      },
    ],
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data: tableData.data || [],
      initialState: { pageIndex: 0 },
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination,
    useRowSelect
  );

  return (
    <div className="p-6">
      <ToastContainer />
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">APE PDF Export History</h1>
      </div>

      <FilterSection
        onFilterChange={handleFilterChange}
        filters={filters}
        pdfTypes={pdfTypes}
        statuses={statuses}
        emailStatuses={emailStatuses}
      />

      {loading ? (
        <Skeleton count={10} height={40} />
      ) : tableData.data.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="overflow-x-auto">
          <table
            {...getTableProps()}
            className="min-w-full divide-y divide-gray-200 bg-white shadow-sm rounded-lg"
          >
            <thead className="bg-gray-50">
              {headerGroups.map((headerGroup) => (
                <tr {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column) => (
                    <th
                      {...column.getHeaderProps(column.getSortByToggleProps())}
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      {column.render('Header')}
                      <span>{column.isSorted ? (column.isSortedDesc ? ' 🔽' : ' 🔼') : ''}</span>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()} className="divide-y divide-gray-200">
              {page.map((row) => {
                prepareRow(row);
                return (
                  <tr {...row.getRowProps()} className="hover:bg-gray-50">
                    {row.cells.map((cell) => (
                      <td {...cell.getCellProps()} className="whitespace-nowrap px-6 py-4 text-sm text-gray-900">
                        {cell.render('Cell')}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* Pagination */}
          <div className="flex items-center justify-between border-t border-gray-200 bg-white px-6 py-3 sm:px-6">
            <div className="flex flex-1 justify-between sm:hidden">
              <button
                onClick={() => previousPage()}
                disabled={!canPreviousPage}
                className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Previous
              </button>
              <button
                onClick={() => nextPage()}
                disabled={!canNextPage}
                className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing <span className="font-medium">{(pageIndex * pageSize) + 1}</span> to{' '}
                  <span className="font-medium">{Math.min((pageIndex + 1) * pageSize, tableData.pagination.total)}</span> of{' '}
                  <span className="font-medium">{tableData.pagination.total}</span> results
                </p>
              </div>
              <div>
                <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                  <button
                    onClick={() => gotoPage(0)}
                    disabled={!canPreviousPage}
                    className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0"
                  >
                    <span className="sr-only">First</span>
                    <ChevronDoubleLeftIcon className="h-5 w-5" aria-hidden="true" />
                  </button>
                  <button
                    onClick={() => previousPage()}
                    disabled={!canPreviousPage}
                    className="relative inline-flex items-center px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0"
                  >
                    <span className="sr-only">Previous</span>
                    <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
                  </button>
                  {pageOptions.map((pageOption) => (
                    <button
                      key={pageOption + 1}
                      onClick={() => gotoPage(pageOption)}
                      className={clsx(
                        "relative inline-flex items-center px-4 py-2 text-sm font-semibold ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0",
                        pageIndex === pageOption
                          ? "z-10 bg-indigo-600 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                          : "text-gray-900"
                      )}
                    >
                      {pageOption + 1}
                    </button>
                  ))}
                  <button
                    onClick={() => nextPage()}
                    disabled={!canNextPage}
                    className="relative inline-flex items-center px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0"
                  >
                    <span className="sr-only">Next</span>
                    <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
                  </button>
                  <button
                    onClick={() => gotoPage(pageCount - 1)}
                    disabled={!canNextPage}
                    className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0"
                  >
                    <span className="sr-only">Last</span>
                    <ChevronDoubleRightIcon className="h-5 w-5" aria-hidden="true" />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApePdfHistoryPage;
